import React, { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { useAuth } from '../context/AuthContext';
import { useCourses } from '../context/CoursesContext';
import { COURSE_DETAILS } from '../components/Sections/CourseDetail';
import { EnrollModal, DOMAIN_COURSES } from '../components/Sections/Courses';
import Navbar from '../components/Layout/Navbar';
import toast from 'react-hot-toast';
import  PartnersMarquee from '../components/Sections/PartnersMarquee';
import Footerpage from '../components/Layout/Footer';
import {
  slugify,
  getInitials,
  getMentor,
  getVideos,
  getTestimonials,
  // PARTNERS,
} from '../data/courseExtras';
import { submitDemoBooking } from '../utils/api';
import siddheshBanner from '../assets/testimonials/siddhesh-banner.jpg';
import sauravBanner from '../assets/testimonials/saurav-banner.jpg';
import pujaBanner from '../assets/testimonials/puja-banner.jpg';
import './CoursePage.css';

// PDF Curriculum Mapping - Maps course titles to their specific PDF files
const getCurriculumPdfPath = (courseTitle) => {
  const title = (courseTitle || '').toLowerCase();
  
  // ===== AI/ML Domain (4 specific courses) =====
  if (title.includes('certified artificial intelligence') && !title.includes('machine learning')) {
    return '/curriculam/ai-ml/certified-artificial-intelligence.pdf.pdf';
  }
  if (title.includes('ai') && title.includes('machine learning')) {
    return '/curriculam/ai-ml/certified-ai-machine-learning.pdf.pdf';
  }
  if (title.includes('generative ai')) {
    return '/curriculam/ai-ml/certified-generative-ai.pdf.pdf';
  }
  if (title.includes('agentic ai')) {
    return '/curriculam/ai-ml/certified-agentic-ai.pdf.pdf';
  }
  
  // ===== Full Stack Domain (4 specific courses) =====
  if (title.includes('frontend development')) {
    return '/curriculam/full-stack/certified-frontend-development.pdf.pdf';
  }
  if (title.includes('backend development')) {
    return '/curriculam/full-stack/certified-backend-development.pdf.pdf';
  }
  if (title.includes('mern stack')) {
    return '/curriculam/full-stack/certified-mern-stack-development.pdf.pdf';
  }
  if (title.includes('java fullstack') || title.includes('java full stack')) {
    return '/curriculam/full-stack/certified-java-fullstack-devlopment.pdf.pdf';
  }
  
  // ===== Data Science Domain (4 specific courses) =====
  if (title.includes('data analyst') && !title.includes('scientist')) {
    return '/curriculam/data-science/certified-data-analyst.pdf.pdf';
  }
  if (title.includes('data scientist')) {
    return '/curriculam/data-science/certified-data-scientist.pdf.pdf';
  }
  if (title.includes('automation')) {
    return '/curriculam/data-science/data-science- automations.pdf.pdf';
  }
  if (title.includes('data science') && title.includes('ai')) {
    return '/curriculam/data-science/data-science-ai.pdf.pdf';
  }
  
  // ===== DevOps & Cloud Domain (4 specific courses) =====
  if (title.includes('cloud solutions') && title.includes('devops')) {
    return '/curriculam/devops-cloud/cloud-solution-devops.pdf.pdf';
  }
  if (title.includes('aws')) {
    return '/curriculam/devops-cloud/certified-aws cloud-practitioner.pdf.pdf';
  }
  if (title.includes('devops engineer')) {
    return '/curriculam/devops-cloud/certified-devops-engineer.pdf.pdf';
  }
  if (title.includes('azure')) {
    return '/curriculam/devops-cloud/microsoft-azure cloud-solutions-pdf.pdf';
  }
  
  // ===== Other Courses (unchanged) =====
  if (title.includes('python') && !title.includes('data') && !title.includes('ai') && !title.includes('ml')) {
    return '/curriculam/python-development.pdf.pdf';
  }
  if (title.includes('c++') || title.includes('c/c++') || title.includes('c programming')) {
    return '/curriculam/c-cpp-programming.pdf.pdf';
  }
  if (title.includes('java') && !title.includes('fullstack') && !title.includes('full stack')) {
    return '/curriculam/java-devlopment.pdf.pdf';
  }
  if (title.includes('mobile') || title.includes('app development') || title.includes('android') || title.includes('ios')) {
    return '/curriculam/mobile-app-devlopment.pdf.pdf';
  }
  if (title.includes('digital marketing') || title.includes('marketing')) {
    return '/curriculam/digital-marketing.pdf.pdf';
  }
  if (title.includes('ui') || title.includes('ux') || title.includes('design')) {
    return '/curriculam/ui-ux-design.pdf.pdf';
  }
  
  // Default fallback - no PDF available
  return null;
};

const buildFallbackDetails = (course) => ({
  icon: course.icon || 'mdi:school',
  tagline: course.tagline || course.desc || 'Professional course by WeIntern',
  price: course.price || 0,
  duration: course.duration || 'Flexible',
  level: (course.level || 'beginner').charAt(0).toUpperCase() + (course.level || 'beginner').slice(1),
  language: course.language || 'English + Hindi',
  certificate: true,
  stipend: true,
  about: course.about || (() => {
    const baseDesc = course.desc || course.tagline || 'This course is designed by industry experts to give you real-world skills and hands-on experience.';
    const toolsList = Array.isArray(course.tools)
      ? course.tools
      : (course.tools || '').split(',').map((t) => t.trim()).filter(Boolean);
    const levelWord = (course.level || 'beginner').toLowerCase();
    return `${baseDesc} Spread across ${course.duration || 'a structured schedule'}, this ${levelWord}-friendly program blends live sessions, hands-on assignments, and mentor support so you can move from fundamentals to real, working projects. Along the way you'll get practical experience with ${toolsList.slice(0, 5).join(', ') || 'industry-standard tools'}, build a portfolio you can show employers, and finish with a certificate that validates what you've learned.`;
  })(),
  tools: Array.isArray(course.tools)
    ? course.tools
    : (course.tools || '').split(',').map((t) => t.trim()).filter(Boolean),
  whatYouLearn: Array.isArray(course.whatYouLearn) && course.whatYouLearn.length
    ? course.whatYouLearn
    : [
        `Build real-world, job-ready skills in ${course.title}`,
        'Work hands-on with the tools and workflows used by industry teams',
        'Complete guided projects you can add to your portfolio',
        'Get mentor support and doubt-clearing sessions throughout',
      ],
  curriculum: [
    { week: 'Phase 1', title: 'Fundamentals & Setup', topics: ['Environment setup', 'Core concepts', 'Basic projects', 'Best practices'] },
    { week: 'Phase 2', title: 'Intermediate Topics', topics: ['Advanced features', 'Real patterns', 'Code review', 'Debugging'] },
    { week: 'Phase 3', title: 'Real Client Projects', topics: ['Client requirements', 'Project execution', 'Delivery', 'Portfolio'] },
  ],
});

const Stars = ({ count }) => (
  <div className="cp-stars">
    {Array.from({ length: 5 }).map((_, i) => (
      <Icon key={i} icon="mdi:star" width={14} height={14} color={i < count ? '#f59e0b' : '#e2e5ee'} />
    ))}
  </div>
);

const CoursePage = () => {
  const { slug } = useParams();
  const { activeCourses } = useCourses();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [enrollCourseData, setEnrollCourseData] = useState(null);
  const [showDemoModal, setShowDemoModal] = useState(false);
  const [activeVideo, setActiveVideo] = useState(null);
  const [currentTestimonialIndex, setCurrentTestimonialIndex] = useState(0);

  const flattenedDomainCourses = Object.values(DOMAIN_COURSES).flat();
  const course =
    activeCourses.find((c) => slugify(c.title) === slug) ||
    flattenedDomainCourses.find((c) => slugify(c.title) === slug);

  // Dynamic Hero Image Selection based on course domain
  const getHeroImage = (courseTitle) => {
    const title = (courseTitle || '').toLowerCase();
    
    // AI/ML Domain
    if (title.includes('ai') || title.includes('ml') || 
        title.includes('artificial intelligence') || title.includes('machine learning') ||
        title.includes('generative') || title.includes('agentic')) {
      return '/course-hero-aiml.jpg';
    }
    
    // Data Science Domain
    if (title.includes('data') && (title.includes('science') || title.includes('scientist') || 
        title.includes('analyst') || title.includes('analytics') || title.includes('automation'))) {
      return '/course-hero-data.jpg';
    }
    
    // DevOps/Cloud Domain
    if (title.includes('devops') || title.includes('cloud') || title.includes('aws') || 
        title.includes('azure') || title.includes('solutions')) {
      return '/course-hero-devops.jpg';
    }
    
    // Other Courses (UI/UX, Digital Marketing, Python, Java, C/C++, Mobile App)
    if (title.includes('ui') || title.includes('ux') || title.includes('design') ||
        title.includes('digital marketing') || title.includes('marketing') ||
        title.includes('python programming') || title.includes('java programming') ||
        title.includes('c/c++') || title.includes('mobile app')) {
      return '/course-hero-other.jpg';
    }
    
    // Full Stack / Web Development (default for frontend, backend, MERN, Java, etc.)
    return '/course-hero-image.jpg';
  };

  if (!course) {
    return (
      <div className="cp-page">
        <Navbar />
        <div className="cp-notfound">
          <h2>Course not found</h2>
          <p>This course may have been removed or renamed.</p>
          <Link to="/" className="cp-btn cp-btn-primary">Back to all courses</Link>
        </div>
      </div>
    );
  }

  const details = COURSE_DETAILS[course.title] || buildFallbackDetails(course);
  const originalPrice = course.originalPrice || Math.round((course.price || details.price) * 1.2);
  const offerPrice = course.price || details.price;

  // Get PDF path for this course
  const curriculumPdfPath = getCurriculumPdfPath(course.title);

  // Display-only: show exact course price as provided
  const discount = Math.round(((originalPrice - offerPrice) / originalPrice) * 100);

  const mentor = getMentor(course);
  const videos = getVideos(course, details);
  const testimonials = getTestimonials(course);

  const handleEnroll = () => {
    if (!user) {
      toast('Please login or register to enroll', { icon: 'ℹ️' });
      navigate('/login');
      return;
    }
    setEnrollCourseData(course);
  };

  return (
    <div className="cp-page">
      <Navbar />

      {/* Hero - Simple Course Info */}
      <div className="cp-hero">
        <div className="cp-hero-inner">
          <div className="cp-hero-left">
            <div className="cp-hero-certified-badge">
              <Icon icon="mdi:certificate" width={20} height={20} />
              <span>CERTIFICATION COURSE</span>
            </div>
            
            <h1 className="cp-hero-course-name">{course.title}</h1>
            <p className="cp-hero-course-tagline">Professional course by WeIntern</p>
            
            <div className="cp-hero-features">
              <div className="cp-hero-feature">
                <Icon icon="mdi:check-circle" width={20} height={20} />
                <span>100% Placement Assistance</span>
              </div>
              <div className="cp-hero-feature">
                <Icon icon="mdi:certificate-outline" width={20} height={20} />
                <span>Industry-Recognized Certificate</span>
              </div>
              <div className="cp-hero-feature">
                <Icon icon="mdi:video-outline" width={20} height={20} />
                <span>Live Interactive Classes</span>
              </div>
              <div className="cp-hero-feature">
                <Icon icon="mdi:briefcase-outline" width={20} height={20} />
                <span>Real-World Projects</span>
              </div>
            </div>
            
            {/* Course Quick Stats */}
            <div className="cp-hero-stats">
              <div className="cp-hero-stat-item">
                <Icon icon="mdi:clock-outline" width={24} height={24} />
                <div className="cp-hero-stat-content">
                  <span className="cp-hero-stat-value">{details.duration || '10 Weeks'}</span>
                  <span className="cp-hero-stat-label">Duration</span>
                </div>
              </div>
              <div className="cp-hero-stat-item">
                <Icon icon="mdi:account-group" width={24} height={24} />
                <div className="cp-hero-stat-content">
                  <span className="cp-hero-stat-value">Live Sessions</span>
                  <span className="cp-hero-stat-label">Interactive Learning</span>
                </div>
              </div>
              <div className="cp-hero-stat-item">
                <Icon icon="mdi:trophy-outline" width={24} height={24} />
                <div className="cp-hero-stat-content">
                  <span className="cp-hero-stat-value">{details.level || 'Beginner'}</span>
                  <span className="cp-hero-stat-label">Skill Level</span>
                </div>
              </div>
            </div>

          </div>

          {/* Hero Image */}
          <div className="cp-hero-right">
            <div className="cp-hero-image-wrapper">
              <img 
                src={getHeroImage(course.title)} 
                alt={course.title} 
                className="cp-hero-image"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Webinar CTA Section - Full Width */}
      <div className="cp-webinar-section">
        <div className="cp-webinar-container">
          <div className="cp-webinar-content">
            <div className="cp-webinar-icon">
              <Icon icon="mdi:video-outline" width={32} height={32} />
            </div>
            <div className="cp-webinar-text">
              <h3 className="cp-webinar-title">Want to Learn More?</h3>
              <p className="cp-webinar-desc">
                Join our free live webinar to explore the course curriculum, meet our instructors, 
                and get all your questions answered in real-time.
              </p>
            </div>
          </div>
          <button 
            className="cp-hero-webinar-btn"
            onClick={() => setShowDemoModal(true)}
          >
            <Icon icon="mdi:calendar-check" width={20} height={20} />
            Book Free Webinar
          </button>
        </div>
      </div>

      {/* Industry Partners */}
      <section>
        <PartnersMarquee/>
      </section>

      <div className="cp-body">
        {/* About */}
        <section className="cp-section">
          <span className="cp-section-eyebrow">Overview</span>
          <h2 className="cp-section-title">About this <span className="cp-title-highlight">course</span></h2>
          <div className="cp-about-card">
            <div className="cp-about-icon">
              <Icon icon="mdi:book-open-page-variant" width={32} height={32} />
            </div>
            <div className="cp-about-content">
              <p className="cp-about-text">{details.about}</p>
              <div className="cp-about-features">
                <div className="cp-about-feature">
                  <Icon icon="mdi:clock-outline" width={20} height={20} />
                  <span>{details.duration || '10 Weeks'}</span>
                </div>
                <div className="cp-about-feature">
                  <Icon icon="mdi:signal" width={20} height={20} />
                  <span>{details.level || 'Beginner'} Level</span>
                </div>
                <div className="cp-about-feature">
                  <Icon icon="mdi:translate" width={20} height={20} />
                  <span>{details.language || 'English + Hindi'}</span>
                </div>
                <div className="cp-about-feature">
                  <Icon icon="mdi:comment-question-outline" width={20} height={20} />
                  <span>Doubt Section</span>
                </div>
                <div className="cp-about-feature">
                  <Icon icon="mdi:laptop" width={20} height={20} />
                  <span>Learn from Live Project</span>
                </div>
                <div className="cp-about-feature">
                  <Icon icon="mdi:code-braces" width={20} height={20} />
                  <span>Practice Section</span>
                </div>
                <div className="cp-about-feature">
                  <Icon icon="mdi:account-tie" width={20} height={20} />
                  <span>1:1 Mentorship</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Certificate Section */}
        <section className="cp-section">
          <span className="cp-section-eyebrow">Certification</span>
          <h2 className="cp-section-title">Get <span className="cp-title-highlight">Certified</span></h2>
          <div className="cp-cert-container">
            <div className="cp-cert-left">
              <h3 className="cp-cert-heading">Certificate Benefits</h3>
              <p className="cp-cert-desc">
                Boost your career with our industry-recognized certification that validates your skills and expertise.
              </p>

              <ul className="cp-cert-list">
                <li><Icon icon="mdi:check-circle" width={18} height={18} /> Industry-recognized certification to enhance your resume</li>
                <li><Icon icon="mdi:check-circle" width={18} height={18} /> Showcase your expertise to potential employers</li>
                <li><Icon icon="mdi:check-circle" width={18} height={18} /> Lifetime access to your digital certificate</li>
                <li><Icon icon="mdi:check-circle" width={18} height={18} /> Shareable on LinkedIn and other professional platforms</li>
                <li><Icon icon="mdi:check-circle" width={18} height={18} /> Increases job prospects and earning potential</li>
              </ul>
            </div>

            <div className="cp-cert-right">
              <div className="cp-cert-image-wrapper">
                <img 
                  src="/certificate-preview.jpeg" 
                  alt="WeIntern Certificate" 
                  className="cp-cert-image"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Exclusive Course Offerings */}
        <section className="cp-section cp-offerings-section">
          <h2 className="cp-offerings-title">Exclusive Course Offerings</h2>
          <div className="cp-offerings-grid">
            <div className="cp-offering-item">
              <div className="cp-offering-icon">📚</div>
              <p className="cp-offering-text">Industry-Oriented Curriculum</p>
            </div>
            <div className="cp-offering-item">
              <div className="cp-offering-icon">💡</div>
              <p className="cp-offering-text">Comprehensive Learning Content</p>
            </div>
            <div className="cp-offering-item">
              <div className="cp-offering-icon">👨‍🏫</div>
              <p className="cp-offering-text">Weekend Live Sessions</p>
            </div>
            <div className="cp-offering-item">
              <div className="cp-offering-icon">🎯</div>
              <p className="cp-offering-text">Capstone Project</p>
            </div>
            <div className="cp-offering-item">
              <div className="cp-offering-icon">💬</div>
              <p className="cp-offering-text">Practice Exercises</p>
            </div>
            <div className="cp-offering-item">
              <div className="cp-offering-icon">📋</div>
              <p className="cp-offering-text">Assignments and Projects</p>
            </div>
            <div className="cp-offering-item">
              <div className="cp-offering-icon">🗣️</div>
              <p className="cp-offering-text">Live Doubt Resolution Sessions</p>
            </div>
            <div className="cp-offering-item">
              <div className="cp-offering-icon">💻</div>
              <p className="cp-offering-text">SME Support Session</p>
            </div>
            <div className="cp-offering-item">
              <div className="cp-offering-icon">📜</div>
              <p className="cp-offering-text">Certification of Completion</p>
            </div>
            <div className="cp-offering-item">
              <div className="cp-offering-icon">👔</div>
              <p className="cp-offering-text">Career Guidance & Interview Preparation</p>
            </div>
            <div className="cp-offering-item">
              <div className="cp-offering-icon">📧</div>
              <p className="cp-offering-text">Email Support</p>
            </div>
            <div className="cp-offering-item">
              <div className="cp-offering-icon">🎙️</div>
              <p className="cp-offering-text">PW Talk Access (Premium & Pro Only)</p>
            </div>
          </div>
        </section>

        {/* Curriculum Download */}
        <section className="cp-section">
          <span className="cp-section-eyebrow">Curriculum</span>
          <h2 className="cp-section-title">AI-Infused <span className="cp-title-highlight">Curriculum</span>, Curated by Experts</h2>
          <div className="cp-curriculum-grid">
            {details.curriculum.map((c, i) => (
              <div className="cp-curriculum-card" key={i}>
                <p className="cp-curriculum-month">{c.week}</p>
                <ul className="cp-curriculum-topics-list">
                  {c.topics.map((topic, j) => (
                    <li className="cp-curriculum-topic" key={j}>{topic}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          {curriculumPdfPath && (
            <div className="cp-curriculum-download-wrap">
              <a
                href={curriculumPdfPath}
                className="cp-btn cp-btn-outline"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Icon icon="mdi:file-download-outline" width={18} height={18} />
                Download Full Curriculum
              </a>
            </div>
          )}
        </section>

        {/* Preview & Mentor - Side by Side */}
        <section className="cp-section">
          <div className="cp-dual-section-grid">
            
            {/* Preview Card */}
            <div className="cp-dual-card">
              <span className="cp-section-eyebrow">Preview</span>
              <h2 className="cp-dual-card-title">See It <span className="cp-title-highlight">Live</span></h2>
              <div className="cp-demo-cta-card">
                <div className="cp-demo-cta-icon">
                  <Icon icon="mdi:calendar-check" width={26} height={26} />
                </div>
                <div className="cp-demo-cta-text">
                  <p className="cp-demo-cta-heading">Want a walkthrough before you enroll?</p>
                  <p className="cp-demo-cta-sub">Book a free live demo session with our team and get your questions about {course.title} answered directly.</p>
                  <div className="cp-demo-cta-features">
                    <span className="cp-demo-cta-feature"><Icon icon="mdi:video-outline" width={16} height={16} /> Live walkthrough</span>
                    <span className="cp-demo-cta-feature"><Icon icon="mdi:message-question-outline" width={16} height={16} /> Ask questions live</span>
                    <span className="cp-demo-cta-feature"><Icon icon="mdi:check-decagram-outline" width={16} height={16} /> No obligation</span>
                  </div>
                </div>
                <button className="cp-btn cp-btn-primary" onClick={() => setShowDemoModal(true)}>
                  <Icon icon="mdi:calendar-check" width={18} height={18} />
                  Book Demo
                </button>
              </div>
            </div>

            {/* Mentor Card */}
            <div className="cp-dual-card">
              <span className="cp-section-eyebrow">Meet your guide</span>
              <h2 className="cp-dual-card-title">Learn from <span className="cp-title-highlight">Experts & Senior Developers</span></h2>
              <div className="cp-mentor-card">
                <div className="cp-mentor-avatar"><Icon icon={mentor.avatarIcon} width={34} height={34} /></div>
                <div className="cp-mentor-info">
                  <h3>{mentor.name}</h3>
                  <p className="cp-mentor-role">{mentor.role} · {mentor.years}+ years experience</p>
                  <p className="cp-mentor-bio">{mentor.bio}</p>
                  <div className="cp-mentor-stats">
                    <span className="cp-mentor-stat"><Icon icon="mdi:school-outline" width={16} height={16} /> Industry Expert</span>
                    <span className="cp-mentor-stat"><Icon icon="mdi:account-group-outline" width={16} height={16} /> 1:1 Doubt Support</span>
                    <span className="cp-mentor-stat"><Icon icon="mdi:rocket-launch-outline" width={16} height={16} /> Real Project Mentorship</span>
                  </div>
                  <a href={`mailto:${mentor.email}`} className="cp-mentor-email">
                    <Icon icon="mdi:email-outline" width={14} height={14} /> {mentor.email}
                  </a>
                </div>
              </div>
            </div>

          </div>
        </section>

        {showDemoModal && (
          <BookDemoModal course={course} onClose={() => setShowDemoModal(false)} />
        )}

        {/* Video Testimonials */}
        <section className="cp-section">
          <span className="cp-section-eyebrow">Real feedback</span>
          <h2 className="cp-section-title">Hear from our <span className="cp-title-highlight">students</span></h2>
          <p className="cp-section-subtitle">Real success stories from our community</p>
          
          {/* Carousel Container */}
          <div className="cp-video-carousel-wrapper">
            <div className="cp-video-carousel">
              {testimonials.map((t, i) => {
                // Real testimonial banner images
                const testimonialBanners = [
                  siddheshBanner,  // Card 1 - Siddhesh
                  sauravBanner,    // Card 2 - Saurav
                  pujaBanner,      // Card 3 - Puja
                ];
                const achievements = [
                  'Placed at Tech Company',
                  'Freelancing Successfully',
                  'Working on Live Projects'
                ];
                
                // Calculate position relative to current index
                let position = i - currentTestimonialIndex;
                if (position < -1) position = testimonials.length + position;
                if (position > 1) position = position - testimonials.length;
                
                const isCenter = position === 0;
                const isVisible = Math.abs(position) <= 1;
                
                return (
                  <div
                    className={`cp-video-carousel-card ${isCenter ? 'cp-video-carousel-card-center' : ''} ${!isVisible ? 'cp-video-carousel-card-hidden' : ''}`}
                    key={i}
                    style={{ order: position }}
                    onClick={() => isCenter && setActiveVideo({
                      videoUrl: (videos[i] || videos[0])?.videoUrl,
                      title: `${t.name} — ${course.title}`,
                    })}
                  >
                    <div className="cp-video-carousel-thumb">
                      <img 
                        src={testimonialBanners[i]} 
                        alt={t.name}
                      />
                      {isCenter && (
                        <>
                          <div className="cp-video-carousel-play">
                            <Icon icon="mdi:play-circle" width={72} height={72} />
                          </div>
                        </>
                      )}
                    </div>
                    {isCenter && (
                      <div className="cp-video-carousel-info">
                        <div className="cp-video-carousel-profile">
                          <img 
                            src={testimonialBanners[i]} 
                            alt={t.name}
                            className="cp-video-carousel-avatar"
                          />
                          <div className="cp-video-carousel-details">
                            <p className="cp-video-carousel-name">{t.name}</p>
                            <p className="cp-video-carousel-course">
                              {t.name === 'Puja' ? 'Certified AI/ML Developer' : course.title}
                            </p>
                          </div>
                          <div className="cp-video-carousel-achievement">
                            {achievements[i % achievements.length]}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Navigation Arrows Below Center Card */}
            <div className="cp-carousel-nav-container">
              <button 
                className="cp-carousel-nav cp-carousel-nav-below"
                onClick={() => {
                  setCurrentTestimonialIndex((prev) => 
                    prev === 0 ? testimonials.length - 1 : prev - 1
                  );
                }}
                aria-label="Previous testimonial"
              >
                <Icon icon="mdi:chevron-left" width={28} height={28} />
              </button>

              {/* Dots Indicator */}
              <div className="cp-carousel-dots">
                {testimonials.map((_, index) => (
                  <button
                    key={index}
                    className={`cp-carousel-dot ${index === currentTestimonialIndex ? 'active' : ''}`}
                    onClick={() => setCurrentTestimonialIndex(index)}
                    aria-label={`Go to testimonial ${index + 1}`}
                  />
                ))}
              </div>

              <button 
                className="cp-carousel-nav cp-carousel-nav-below"
                onClick={() => {
                  setCurrentTestimonialIndex((prev) => 
                    prev === testimonials.length - 1 ? 0 : prev + 1
                  );
                }}
                aria-label="Next testimonial"
              >
                <Icon icon="mdi:chevron-right" width={28} height={28} />
              </button>
            </div>
          </div>
        </section>

        {activeVideo && (
          <div className="modal-overlay" onClick={() => setActiveVideo(null)}>
            <div
              onClick={(e) => e.stopPropagation()}
              style={{ background: '#fff', borderRadius: 16, padding: 20, maxWidth: 640, width: '90%' }}
            >
              <button
                className="cp-demo-modal-close"
                onClick={() => setActiveVideo(null)}
                aria-label="Close"
                style={{ marginBottom: 10 }}
              >
                <Icon icon="mdi:close" width={20} height={20} />
              </button>
              <iframe
                src={activeVideo.videoUrl}
                title={activeVideo.title}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                style={{ width: '100%', height: '360px', borderRadius: 12, display: 'block' }}
              />
              <p style={{ marginTop: 12, fontWeight: 600, color: '#1a2036' }}>{activeVideo.title}</p>
            </div>
          </div>
        )}
        {/* Bottom CTA */}
        <div className="cp-bottom-cta">
          <div>
            <span className="cp-price-new" style={{ fontSize: '1.4rem' }}>₹{offerPrice.toLocaleString('en-IN')}</span>
            <span className="cp-price-old" style={{ marginLeft: 10 }}>₹{Number(originalPrice).toLocaleString('en-IN')}</span>
          </div>
          <button className="cp-btn cp-btn-primary" onClick={handleEnroll}>
            Enroll Now <Icon icon="mdi:arrow-right" width={16} height={16} />
          </button>
        </div>
      </div>

      {enrollCourseData && (
        <EnrollModal course={enrollCourseData} onClose={() => setEnrollCourseData(null)} />
      )}

      <Footerpage />
    </div>
  );
};

/* ─────────────────────────────────────────────
   Book a Demo — lead-capture modal.
   Submits live to the backend (no static/dummy data);
   the entry appears immediately under Admin Dashboard
   → Applications → Demo Bookings.
────────────────────────────────────────────── */
const BookDemoModal = ({ course, onClose }) => {
  const { user } = useAuth();
  const [form, setForm] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    college: user?.college || '',
    domain: course?.title || '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!form.name.trim() || !form.email.trim() || !form.phone.trim() || !form.college.trim() || !form.domain.trim()) {
      setError('Please fill in all fields.');
      return;
    }

    setSubmitting(true);
    try {
      await submitDemoBooking({
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        college: form.college.trim(),
        domain: form.domain.trim(),
        courseTitle: course?.title || '',
      });
      setSubmitted(true);
      toast.success("Demo request sent! We'll reach out to you shortly.");
    } catch (err) {
      const msg = err.response?.data?.message || 'Something went wrong. Please try again.';
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="cp-demo-modal" onClick={(e) => e.stopPropagation()}>
        <button className="cp-demo-modal-close" onClick={onClose} aria-label="Close">
          <Icon icon="mdi:close" width={20} height={20} />
        </button>

        {submitted ? (
          <div className="cp-demo-success">
            <Icon icon="mdi:check-circle" width={52} height={52} color="#2563eb" />
            <h3>Request received!</h3>
            <p>Our team will contact you shortly to schedule your live demo for {course.title}.</p>
            <button className="cp-btn cp-btn-primary" onClick={onClose}>Close</button>
          </div>
        ) : (
          <>
            <h3 className="cp-demo-modal-title">Book a Free Demo</h3>
            <p className="cp-demo-modal-sub">Fill in your details and our team will schedule a live walkthrough of {course.title}.</p>

            <form className="cp-demo-form" onSubmit={handleSubmit}>
              <label className="cp-demo-field">
                <span>Full Name</span>
                <input type="text" name="name" value={form.name} onChange={handleChange} placeholder="Your full name" required />
              </label>
              <label className="cp-demo-field">
                <span>Email</span>
                <input type="email" name="email" value={form.email} onChange={handleChange} placeholder="you@example.com" required />
              </label>
              <label className="cp-demo-field">
                <span>Phone Number</span>
                <input type="tel" name="phone" value={form.phone} onChange={handleChange} placeholder="10-digit mobile number" required />
              </label>
              <label className="cp-demo-field">
                <span>College</span>
                <input type="text" name="college" value={form.college} onChange={handleChange} placeholder="Your college / university" required />
              </label>
              <label className="cp-demo-field">
                <span>Domain</span>
                <input type="text" name="domain" value={form.domain} onChange={handleChange} placeholder="e.g. Full Stack Development" required />
              </label>

              {error && <p className="cp-demo-error">{error}</p>}

              <button type="submit" className="cp-btn cp-btn-primary cp-demo-submit" disabled={submitting}>
                {submitting ? 'Submitting...' : 'Book Demo'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default CoursePage;